import {get} from "lodash";
import {getBoolean, getInteger, getObject, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'recommendations';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@recommendations/reset',
	SET_LIST_DATA: '@recommendations/set_list_data',
	SET_LIST_FILTER: '@recommendations/set_list_filter',
	CLEAR_LIST_DATA: '@recommendations/clear_list_data',
	SET_ITEM: '@recommendations/set_item',
	CLEAR_ITEM: '@recommendations/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} recommendationsListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setRecommendationsListData: recommendationsListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: recommendationsListData
	}),
	setRecommendationsListFilter: recommendationsListFilter => ({
   		type: REDUCER_TYPES.SET_LIST_FILTER, 
   		filter: recommendationsListFilter
   	}),
	clearRecommendationsListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {RecommendationsItemDataObject} recommendationsItem
	 * @return {{type: string, item: RecommendationsItemDataObject}}
	 */
	setRecommendationsItem: recommendationsItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: recommendationsItem
	}),
	clearRecommendationsItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {RecommendationsItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.SET_LIST_FILTER: return {
			...state, 
			listData: {...getObject(state.listData), filter: action.filter}
		};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getRecommendationsList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getRecommendationsListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getRecommendationsListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getRecommendationsListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getRecommendationsItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;